import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api, usd, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { useOnline } from "@/lib/pwa";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Trash2, ArrowUpRight, Ruler, MapPin, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { billing } = useBilling();
  const online = useOnline();
  const [offlineMode, setOfflineMode] = useState(false);
  const [projects, setProjects] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      setOfflineMode(false);
      try { localStorage.setItem("c2b_projects", JSON.stringify(data)); } catch { /* quota */ }
    } catch {
      const cached = localStorage.getItem("c2b_projects");
      if (cached) {
        setProjects(JSON.parse(cached));
        setOfflineMode(true);
      } else {
        setProjects([]);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.project_id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Could not delete project");
    }
  };

  const totalValue = (projects || []).reduce((s, p) => s + (p.estimate?.summary?.total_cost || 0), 0);

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        {(offlineMode || !online) && (
          <div data-testid="offline-banner" className="mb-6 border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 font-mono2 text-xs uppercase tracking-widest text-amber-300">
            Offline — showing your saved projects. New estimates need a connection.
          </div>
        )}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Customer Portal</p>
            <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">
              {user?.name?.split(" ")[0] || "Your"} projects
            </h1>
            {billing && (
              <div className="mt-2 flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest">
                <span className="border border-border bg-secondary/40 px-2 py-0.5 text-primary" data-testid="plan-badge">{billing.plan_name} plan</span>
                {billing.credits > 0 && <span className="text-muted-foreground">{billing.credits} credits</span>}
                {billing.project_limit != null && (
                  <span className="text-muted-foreground">{billing.projects_used}/{billing.project_limit} projects</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {billing?.plan === "free" && (
              <Link to="/billing" data-testid="dashboard-upgrade-btn">
                <Button variant="outline" className="rounded-sm border-primary/60 font-mono2 uppercase tracking-widest text-primary hover:bg-primary/10">
                  <Zap className="mr-1 h-4 w-4" /> Upgrade
                </Button>
              </Link>
            )}
            <Link to="/builder" data-testid="new-build-btn">
              <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                <Plus className="mr-1 h-4 w-4" /> New Build
              </Button>
            </Link>
          </div>
        </div>

        {/* stats */}
        <div className="mt-8 grid grid-cols-2 border border-border sm:grid-cols-3">
          {[
            ["Projects", projects ? projects.length : "—"],
            ["Portfolio value", projects ? usd(totalValue) : "—"],
            ["Account", user?.email || ""],
          ].map(([l, v], i) => (
            <div key={i} className="border-r border-border p-5 last:border-r-0">
              <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
              <div className="mt-1 truncate font-mono2 text-lg font-semibold text-foreground">{v}</div>
            </div>
          ))}
        </div>

        {/* list */}
        <div className="mt-10">
          {projects === null ? (
            <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
          ) : projects.length === 0 ? (
            <div className="border border-dashed border-border py-24 text-center">
              <Ruler className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
              <p className="font-heading text-xl font-semibold">No projects yet</p>
              <p className="mt-1 text-muted-foreground">Design your first build to see an instant cost estimate.</p>
              <Link to="/builder" className="mt-6 inline-block" data-testid="empty-new-build">
                <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  <Plus className="mr-1 h-4 w-4" /> Create a Build
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((p, i) => (
                <motion.div key={p.project_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="group flex flex-col border border-border bg-card/40 transition-colors hover:border-primary/50"
                  data-testid={`project-card-${i}`}>
                  <div className="flex items-start justify-between border-b border-border p-5">
                    <div className="min-w-0">
                      <h3 className="truncate font-heading text-lg font-semibold">{p.name}</h3>
                      <p className="mt-0.5 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
                        {p.spec?.building_type?.replace(/_/g, " ")}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-destructive" data-testid={`delete-project-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="border-border bg-popover">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete “{p.name}”?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently removes the project and its estimate.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(p.project_id)} data-testid={`confirm-delete-${i}`}
                            className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <div className="flex-1 p-5">
                    <div className="flex items-center gap-4 font-mono2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {p.spec?.square_footage} sqft</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.spec?.region}</span>
                    </div>
                    <div className="mt-4 font-mono2 text-2xl font-bold text-primary">
                      {money(p.estimate?.summary?.total_cost, p.estimate?.summary?.currency_symbol || "$")}
                    </div>
                    <p className="font-mono2 text-xs text-muted-foreground">
                      {money(p.estimate?.summary?.cost_per_unit ?? p.estimate?.summary?.cost_per_sqft, p.estimate?.summary?.currency_symbol || "$")} / {p.estimate?.summary?.unit_label || "sqft"}
                    </p>
                  </div>
                  <Link to={`/projects/${p.project_id}`} data-testid={`open-project-${i}`}
                    className="flex items-center justify-between border-t border-border px-5 py-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground transition-colors group-hover:text-primary">
                    View blueprint & scope <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
